import { Fragment, useCallback, useMemo, useState, useEffect } from "react";

import { useMutation } from "@apollo/client";
import { FormikProvider, useFormik } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";

import AutoSave from "pages/profile/AutoSave";

import CreateLawyerSelect from "components/ProfileEditor/parties/lawyer/CreateLawyerSelect";
import LawyerFormInputs from "components/ProfileEditor/parties/lawyer/LawyerFormInputs";
import {
  getInitialValues,
  prepareVariablesForUpdateLawyer,
  useDetectChanges,
  useOrgLawyers,
  useResetLawyerForm,
  useSharedLawyersQuery,
} from "components/ProfileEditor/parties/lawyer/lawyerFormHelpers";
import difference from "components/calculations/utils/difference";
import Button from "components/common/Button";
import ToggleButtons from "components/common/ToggleButtons";
import CustomSelect from "components/common/inputs/Select/Select";

import useMe from "hooks/useMe";

import getUserName from "utils/getUserName";

import UPDATE_CLIENT from "@gql/mutations/clients/updateClient";

import UpdateLawyerInDatabaseButton from "./UpdateLawyerInDatabaseButton";

const LawyerForm = ({
  user,
  entityObject,
  entityType,
  lawyerObject,
  hasIsSelfRepresented,
  onSaved,
}) => {
  const { me } = useMe();

  const [updateClient, { loading: clientLoading }] = useMutation(UPDATE_CLIENT);

  const [hasChanges, setHasChanges] = useState(false);

  const initialValues = useMemo(
    () =>
      getInitialValues(
        lawyerObject,
        hasIsSelfRepresented && entityObject ? entityObject.isSelfRepresented : null,
      ),
    [lawyerObject, hasIsSelfRepresented, entityObject],
  );

  const onSubmit = useCallback(
    (values) => {
      const diff = difference(
        values,
        getInitialValues(
          lawyerObject,
          hasIsSelfRepresented ? entityObject.isSelfRepresented : null,
        ),
      );

      const updatedFieldsLength = Object.keys(diff).length;

      if (updatedFieldsLength === 0) return null;

      const hasIsSelfRepresentedField = Object.hasOwn(diff, "isSelfRepresented");

      if (updatedFieldsLength !== 0 && !hasIsSelfRepresentedField) {
        if (!entityObject?.id || !values?.name) return null;
      }

      const { variables } = prepareVariablesForUpdateLawyer({
        values,
        entityType,
        entityObject,
        user,
        hasIsSelfRepresented,
      });

      updateClient({ variables })
        .then(() => {
          toast.success(`${entityType} lawyer information has been successfully changed`);
          if (typeof onSaved === "function") onSaved();
        })
        .catch((error) => {
          console.error("Error in handleSubmitForm", error);
          toast.error("Failed to save data");
        });
    },
    [updateClient, entityType, user, entityObject, lawyerObject, hasIsSelfRepresented, onSaved],
  );

  const formik = useFormik({ initialValues, onSubmit, enableReinitialize: true });

  const { handleSubmit, setFieldValue, values, setValues } = formik;

  useEffect(() => {
    // Do nothing when the party is marked as self-represented.
    if (values?.isSelfRepresented) return;

    const currentUserName = getUserName(me) || me?.professional?.profile?.email;

    // Only update the form if the selected lawyer matches the logged-in user.
    if (values?.name !== currentUserName) return;

    const newFax = me?.professional?.profile?.fax || "";
    const newPhone = me?.professional?.profile?.phone || "";
    const newEmail = me?.professional?.profile?.email || "";

    if (
      values?.location?.fax !== newFax ||
      values?.location?.phone !== newPhone ||
      values?.location?.email !== newEmail
    ) {
      setValues({
        ...values,
        location: {
          ...values.location,
          fax: newFax,
          phone: newPhone,
          email: newEmail,
        },
      });
    }
    // We deliberately omit `values.location` from the dependency array to avoid
    // unnecessary effect executions when only unrelated fields change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    me?.professional?.profile?.fax,
    me?.professional?.profile?.phone,
    me?.professional?.profile?.email,
    values?.name,
  ]);

  const { isSelfRepresented } = values;

  const handleChangeIsSelfRepresented = useCallback(
    (value) => {
      setFieldValue("isSelfRepresented", value);
      if (value === true) {
        setFieldValue("sanityId", "");
        setFieldValue("name", "");
        setFieldValue("orgName", "");
        setFieldValue("location", {
          email: "",
          city: "",
          phone: "",
          fax: "",
          postal: "",
          street1: "",
          street2: "",
          country: "",
          residence: "",
        });
      }
    },
    [setFieldValue],
  );

  const { handleResetForm } = useResetLawyerForm({
    userId: user.client?.id,
    entityType,
    entityObject,
    lawyerObject,
    updateClient,
  });

  const {
    sharedLawyers,
    loading: sharedLawyersLoading,
    refetch,
  } = useSharedLawyersQuery({ user, entityObject });

  useDetectChanges({ values, hasChanges, setHasChanges, lawyers: sharedLawyers });

  const { loading, lawyers, handleSelectChange, selectValue } = useOrgLawyers({
    entityObject,
    values,
    setValues,
    userId: me.id,
  });

  return (
    <FormikProvider value={formik}>
      <AutoSave />
      <Form className="lawyer-form" onSubmit={handleSubmit}>
        <Row>
          {hasIsSelfRepresented && (
            <Col xs={6}>
              <ToggleButtons
                label="Is this Party Self-Represented?"
                name="isSelfRepresented"
                buttons={[
                  { value: true, label: "Self-Represented" },
                  { value: false, label: "Has Counsel" },
                ]}
                className="mt-3"
                onChange={handleChangeIsSelfRepresented}
              />
            </Col>
          )}

          {/* Org lawyers select if entity.isOurClient: true */}
          {!isSelfRepresented && entityObject?.isOurClient && (
            <Col xs={6}>
              <div className="form-group custom-input mt-3">
                <label className="form-control-label">Professionals</label>
                <CustomSelect
                  className="multi-select"
                  onChange={handleSelectChange}
                  name="professionals"
                  placeholder="Unassigned"
                  value={selectValue}
                  options={lawyers}
                  isLoading={loading}
                  disabled={clientLoading}
                />
              </div>
            </Col>
          )}

          {/* Shared|Sanity lawyers select if entity.isOurClient: false */}
          {!isSelfRepresented && !entityObject?.isOurClient && (
            <Col xs={6}>
              <CreateLawyerSelect
                user={user}
                lawyers={sharedLawyers}
                refetch={refetch}
                disabled={sharedLawyersLoading}
              />
            </Col>
          )}
        </Row>

        {!isSelfRepresented && (
          <Fragment>
            <LawyerFormInputs clientLoading={clientLoading} />

            <Row>
              <Col xs={6}>
                {lawyerObject?.id && (
                  <Button onClick={handleResetForm} variant="danger" disabled={clientLoading}>
                    Reset
                  </Button>
                )}
              </Col>
              <Col xs={6}>
                {hasChanges && (
                  <UpdateLawyerInDatabaseButton
                    user={user}
                    lawyerObject={lawyerObject}
                    values={values}
                  />
                )}
              </Col>
            </Row>
          </Fragment>
        )}
      </Form>
    </FormikProvider>
  );
};

export default LawyerForm;
